
import { hexToRGB, rgbToHex } from "./ColorMath";
import { attributeTypes } from "../Definitions/AttributeTypes";

import { getNodeComponentAttributeValue, AttributeCollection, getNodeStyleAttributeValue } from "../Definitions/LayoutNode";
import { ScreenSize } from "../Definitions/ScreenSize";
import { SiteStyling } from "../Definitions/SiteStyling";
import { FontBreakPoints, GetAdjustedFontSizeFromSiteStyling, GetFontScaleCSSLine } from "./FontScalingUtils";



export const serifFonts = [
    "Neuton", "Noto Serif","Playfair Display","Della Respira","Bitter","DM Sans","Tangerine","Palatino Linotype","Palatino"
    ]



export default function generateGlobalStyleFromSiteData(siteStyling: SiteStyling,rootSiteName:string = ".v-site",isLivePublish:boolean = false){
    let returnStyle = "";

    returnStyle +=":root{\n";

    const fontA = siteStyling.fontPrimary&&siteStyling.fontPrimary.length>0?siteStyling.fontPrimary:"proxima-nova";
    const fontB = siteStyling.fontSecondary&&siteStyling.fontSecondary.length>0?siteStyling.fontSecondary:"proxima-nova";

    const fontAFallback  = (serifFonts.includes(fontA))?"serif":"sans-serif";
    const fontBFallback  = (serifFonts.includes(fontB))?"serif":"sans-serif";

    if(siteStyling.colorPallet)
    for(let i = 0;i< siteStyling.colorPallet.length;i++){
        returnStyle += "--theme-color-"+(i+1)+":"+ siteStyling.colorPallet[i]+"; \n";

    }

    if(siteStyling.imageMapping)
    for(let i = 0;i< siteStyling.imageMapping.length;i++){

        returnStyle +="--image-"+(i+1)+": url("+siteStyling.imageMapping[i]+");\n";
    }

    const pageWidthString = getNodeComponentAttributeValue(siteStyling.pageWidth,"pageWidth","1200px");
    const bgColor = getNodeComponentAttributeValue(siteStyling.pageColor,"pageColor","#ffffff");
    const fColor = getNodeComponentAttributeValue(siteStyling.pageColor,"pageFontColor","#000000");
    const defaultPadding = getNodeComponentAttributeValue(siteStyling.pageWidth,"defaultPadding","12px");
    const starColor = getNodeComponentAttributeValue(siteStyling.misc,"starColor","#f6d645"); //
    const pageWidthNum = parseInt(pageWidthString);

    const btnFontColorA = getNodeStyleAttributeValue(siteStyling.buttonPrimary,ScreenSize.Desktop,"color",fColor);
    const btnFontColorB = getNodeStyleAttributeValue(siteStyling.buttonSecondary,ScreenSize.Desktop,"color",fColor);
    const btnFontColorC = getNodeStyleAttributeValue(siteStyling.buttonTertiary,ScreenSize.Desktop,"color",fColor);
    let halfWidth = "600px";
    if(pageWidthNum){
        halfWidth = (pageWidthNum/2).toString()+"px";

    }
    returnStyle += "--half-width:"+halfWidth+"; \n";
    returnStyle += "--page-width:"+pageWidthString+"; \n";
    returnStyle += "--default-padding:"+defaultPadding+";\n";
    returnStyle += "--default-bg-color:"+bgColor+";\n";
    returnStyle += "--default-font-color:"+fColor+";\n";
    returnStyle +=`--wbHeadFontFamily:'${fontA}',${fontAFallback};\n`;
    returnStyle +=`--wbHeadBodyFamily:'${fontB}',${fontBFallback};\n`;
    returnStyle +="}\n";

    returnStyle+= rootSiteName+" { background-color:"+bgColor+"; color:"+fColor+"; }\n";

    returnStyle += createClassStyleFromAttributeCollection(rootSiteName+" a:not(.no-a):not(.v-button):not(.dropdown-item), .v-link",siteStyling.link);

    returnStyle += ".v-filled-star{ color:"+starColor+" !important; }\n";
    //Buttons and their hover/active states
    returnStyle += createClassStyleFromAttributeCollection(".v-button.primary",siteStyling.buttonPrimary);
    returnStyle += createClassStyleFromAttributeCollection(".v-button.secondary",siteStyling.buttonSecondary);
    returnStyle += createClassStyleFromAttributeCollection(".v-button.tertiary",siteStyling.buttonTertiary);

    returnStyle += getButtonHoverStyle(".v-button.primary:hover",btnFontColorA, siteStyling,siteStyling.buttonPrimary);
    returnStyle += getButtonHoverStyle(".v-button.secondary:hover",btnFontColorB,siteStyling,siteStyling.buttonSecondary);
    returnStyle += getButtonHoverStyle(".v-button.tertiary:hover",btnFontColorC,siteStyling,siteStyling.buttonTertiary);

    returnStyle += getButtonHoverStyle(".v-button-hover.primary",btnFontColorA,siteStyling,siteStyling.buttonPrimary);
    returnStyle += getButtonHoverStyle(".v-button-hover.secondary",btnFontColorB,siteStyling,siteStyling.buttonSecondary);
    returnStyle += getButtonHoverStyle(".v-button-hover.tertiary",btnFontColorC,siteStyling,siteStyling.buttonTertiary);

    returnStyle += getButtonHoverStyle(".v-button.primary:active",btnFontColorA,siteStyling,siteStyling.buttonPrimary,20);
    returnStyle += getButtonHoverStyle(".v-button.secondary:active",btnFontColorB,siteStyling,siteStyling.buttonSecondary,20);
    returnStyle += getButtonHoverStyle(".v-button.tertiary:active",btnFontColorC,siteStyling,siteStyling.buttonTertiary,20);

    returnStyle += getButtonHoverStyle(".v-button-active.primary",btnFontColorA,siteStyling,siteStyling.buttonPrimary,20);
    returnStyle += getButtonHoverStyle(".v-button-active.secondary",btnFontColorB,siteStyling,siteStyling.buttonSecondary,20);
    returnStyle += getButtonHoverStyle(".v-button-active.tertiary",btnFontColorC,siteStyling,siteStyling.buttonTertiary,20);


    //Main Fonts
    returnStyle +=rootSiteName+` { font-family:'${fontB}',${fontBFallback}; }\n`;
    returnStyle +=`${rootSiteName} h1, ${rootSiteName} h2, ${rootSiteName} h3, ${rootSiteName} h4, .review-text-head .v-navigation-list li, .v-navigation-bar{ font-family:'${fontA}',${fontAFallback}; }\n`;//include nav items as well in header font styles


    //headings
    returnStyle += createClassStyleFromAttributeCollection(rootSiteName+" h1",siteStyling.heading1);
    returnStyle += createClassStyleFromAttributeCollection(rootSiteName+" h2",siteStyling.heading2);
    returnStyle += createClassStyleFromAttributeCollection(rootSiteName+" h3",siteStyling.heading3);
    returnStyle += createClassStyleFromAttributeCollection(rootSiteName+" h4",siteStyling.heading4);

    //paragraphs
    returnStyle += createClassStyleFromAttributeCollection(rootSiteName+" .vp-1",siteStyling.paragraph1);
    returnStyle += createClassStyleFromAttributeCollection(rootSiteName+" .vp-2",siteStyling.paragraph2);
    returnStyle += createClassStyleFromAttributeCollection(rootSiteName+" .vp-3",siteStyling.paragraph3);

    returnStyle += GetFontScalingStyle(siteStyling,rootSiteName,pageWidthNum);
    //stenciling last to ensure overrides
    returnStyle +=`
    .stenciled .stencil-item, .stenciled .v-button, .stenciled vp-1, .stenciled .vp-2, .stenciled .vp-3, .stenciled h1, .stenciled h2, .stenciled h3, .stenciled h4{
        animation: stencil-anim 1s ease-in-out infinite;
        background-size: 200% 200%;
        background-image: linear-gradient(90deg, #888, #aaa, #888, #aaa) !important;
        background-size: 400% 400% !important;
        border-radius: 3px;
        opacity: .3 !important;
        pointer-events: none !important;
        color: transparent !important;
        user-select: none !important;
    }

    .stenciled .stencil-item.max-content{
        width: max-content !important;
    }

    .stenciled.heavy .stencil-item{
        opacity : .8 !Important;
    }

    .stenciled  h3, .stenciled  h4{
        width:max-content !important;
    }

    `

    for (let i = 0; i < siteStyling.customFonts.length; i++) {
        let fontDataStr = siteStyling.customFonts[i];
        if(fontDataStr && fontDataStr.startsWith("https://")){
            continue;
        }
        if (fontDataStr && fontDataStr.length >= 3) {
            try {
                let parsed = JSON.parse(fontDataStr);
                if (parsed && parsed.cssDeclaration) {

                    returnStyle += parsed.cssDeclaration + "\n";
                }
            }
            catch (e) {
                console.warn("Error parsing custom font data at index " + i + ": ", e);
            }

        }
    }



    return returnStyle;



}

function getButtonHoverStyle(baseClass:string,fontColor:string,siteStyling:SiteStyling,btnCollection:AttributeCollection,influence = 15){

    let color = getNodeStyleAttributeValue(btnCollection,ScreenSize.Desktop,attributeTypes.backgroundColor,"#ffffff");
    if(color.includes("var(")){
        let num = parseInt(color.replace("var(--theme-color-","").replace(")",""));
        if(num){

            color = siteStyling.colorPallet[num-1];
        }
    }

    let rgba = hexToRGB(color);
    const lightness = (rgba.r+rgba.g+rgba.b)/3;
    const adjustment = lightness>128? -influence:influence;

    rgba.r  = Math.min(255,Math.max(0,rgba.r + adjustment));
    rgba.g = Math.min(255,Math.max(0,rgba.g + adjustment));
    rgba.b = Math.min(255,Math.max(0,rgba.b + adjustment));

    return baseClass+"{ background-color:rgba("+rgba.r+","+rgba.g+","+rgba.b+",1); color:"+fontColor+" } \n";


}

export function GetHexFromColorValue(color:string,siteStyling:SiteStyling){
    if(color.includes("var(")){
        let num = parseInt(color.replace("var(--theme-color-","").replace(")",""));
        if(!isNaN(num)){

            color = siteStyling.colorPallet[num-1];
        }
    }
    return color;

}

export function BrightenOrDarkenColor(color:string,siteStyling:SiteStyling,amount:number){
    color = GetHexFromColorValue(color,siteStyling);
    let rgba = hexToRGB(color);
    const lightness = (rgba.r+rgba.g+rgba.b)/3;
    const adjustment = lightness>128? -amount:amount;
    rgba.r  = Math.min(255,Math.max(0,rgba.r + adjustment));
    rgba.g = Math.min(255,Math.max(0,rgba.g + adjustment));
    rgba.b = Math.min(255,Math.max(0,rgba.b + adjustment));

    return rgbToHex(rgba.r,rgba.g,rgba.b,1);
}


function createClassStyleFromAttributeCollection(rootClass:string,collection:AttributeCollection,screen:ScreenSize = ScreenSize.Desktop){


    let styleString = `${rootClass} { `;

    for(const [key,value] of Object.entries(collection.screenSizeStyleAttributes[screen])){

        let newKey  = reactToCss(key);
        styleString += `${newKey}:${value}; \n`;

    }

    return styleString + "}\n";

}

export function GetBlogStyling(blogAttributes:AttributeCollection){



    let layout = getNodeComponentAttributeValue(blogAttributes,"blog-hero-layout","");

    let returnString = "";
    //for blogging, only include the neccissary overrides to apply the correct styling based on selected layout, let server render rest of required styling to avoid including anything unnecessary
    if(layout == "overlay"){

        returnString = `
.v-site .blog-overlay{
    pointer-events: none;
    z-index: 1;
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    display: block;
    background:linear-gradient(0deg,var(--default-bg-color) 24%, transparent 85%);
}

.blog-heading{
        position:relative;
}

.v-site .blog-heading-image{
    max-width: none;
    height:500px;
}

.v-site .blog-details-wrapper {
    position: absolute;
    bottom: 20px;
    z-index: 5;
    text-shadow: 0 0 10px var(--default-bg-color);
}
        `


    }
    else if(layout == ""){
        returnString =  ` .v-site .blog-heading-image{margin-top:2em;}`;
    }

    else if(layout=="full"){
        returnString =  `
.v-site .blog-heading-image{
    max-width: none;
}
        `
    }
    else if(layout=="below"){
        returnString =  `
.v-site .blog-header{
    flex-direction: column-reverse !important;
}
        `
    }


    // "blog-show-table-contents",
    // "blog-show-categories",
    // "blog-show-tags",
    // "blog-show-author",
    // "blog-show-date",
    // "blog-show-readtime",
    // "blog-image-radius",

    let hideCategories = getNodeComponentAttributeValue(blogAttributes,"blog-show-categories","")=="hide";
    let hideTags = getNodeComponentAttributeValue(blogAttributes,"blog-show-tags","")=="hide";
    let hideAuthor = getNodeComponentAttributeValue(blogAttributes,"blog-show-author","")=="hide";
    let hideProfileImg = getNodeComponentAttributeValue(blogAttributes,"blog-show-author","")=="name-only";
    let hideDate = getNodeComponentAttributeValue(blogAttributes,"blog-show-date","")=="hide";
    let hideReadTime = getNodeComponentAttributeValue(blogAttributes,"blog-show-readtime","")=="hide";
    let imageRadius = getNodeComponentAttributeValue(blogAttributes,"blog-image-radius","0px");

    if(imageRadius){
        returnString += `
        .blog-page-root .rich-text img,  .blog-heading-image {
            border-radius: ${imageRadius};
        }
        `
    }
    if(hideAuthor || hideProfileImg){
        returnString += `
.blog-author-img{
    display:none;
}
        `
    }

    if(hideAuthor){
        returnString += `
.blog-author-text{
    display:none;
}
`
    }

    if(hideTags){
        returnString += `
.blog-chip.tag{
    display:none;
}
`
    }
    if(hideCategories){
        returnString += `
.blog-chip.category{
    display:none;
}
`
    }

    if(hideDate){
        returnString += `
.blog-date{
    display:none;
}
`
    }

    if(hideReadTime){
        returnString += `
.blog-read-time{
    display:none;
}
`
    }

    if(hideDate || hideReadTime){
        returnString += `
.blog-time-divider{
    display:none;
    }
`
    }



    return returnString.replaceAll("\n","").trim();
}

function GetFontScalingStyle(siteStyling:SiteStyling,rootSiteName:string = ".v-site",defaultPageWidth:number = 1200){

    if(!siteStyling.useFontScaling){



        return `
        ${rootSiteName} { font-size: ${siteStyling.desktopFontSize}px; }
        @media (max-width: ${FontBreakPoints.Tablet}px) {   ${rootSiteName} { font-size:${siteStyling.tabletFontSize}px; } }
        @media (max-width: ${FontBreakPoints.Mobile}px) {   ${rootSiteName} { font-size:${siteStyling.tabletFontSize}px; } }
        `;
    }

    let sizes = GetAdjustedFontSizeFromSiteStyling(siteStyling);
    return `
                                                ${rootSiteName} { font-size:${GetFontScaleCSSLine(sizes.desktop,defaultPageWidth  )}; }
@media (max-width: ${FontBreakPoints.Tablet}px) {   ${rootSiteName} { font-size:${GetFontScaleCSSLine(sizes.tablet, FontBreakPoints.Tablet)}; } }
@media (max-width: ${FontBreakPoints.Mobile}px) {   ${rootSiteName} { font-size:${GetFontScaleCSSLine(sizes.mobile, FontBreakPoints.Mobile)}; } }
    `;


}


function reactToCss(reactCss:string){
    return reactCss.replace(/[A-Z]/g, match => `-${match.toLowerCase()}`);
  };